import type { ReactNode } from 'react';

import type { JSONContent } from '@tiptap/core';

import { useDreamComponentsContext } from '@/dreamComponentProvider/hooks';
import { PostCardActionsElement, type TPostCardActionsElement } from '@/postCardActionsElement';
import type { SerializableNode, SerializerProps } from '@/serializer';
import type { PostData, PostInsightsData } from '@/types';
import { formatPostRank, formatPostTime, updateText } from '@/utils/post/utils';

import { ContentTags } from './components/ContentTags';
import { PaidContent } from './components/PaidContent';
import { PostAuthors } from './components/PostAuthors';

import { getStructureElement } from '../../serializer/getStructureElement';
import { SerializeTextLeaf, type TTextLeaf } from '../../textLeaf';
import type { TPostElement } from '../Post.types';

interface ExtendedSerializerProps extends SerializerProps {
  post: PostData;
  postIndex?: number;
  postInsights?: PostInsightsData;
  node: JSONContent & SerializableNode;
  isLive: boolean;
  onLikeClick?: (postId: string) => void;
  onCommentClick?: (postSlug: string) => void;
  postNode?: TPostElement;
  publicationTimeZone?: string;
  showCommentsButton?: boolean;
}

const serializeNode = (
  {
    node,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    createKey = (entry) => entry[1].join(','),
    children,
    post,
    postIndex,
    postInsights,
    isLive,
    onLikeClick,
    onCommentClick,
    postNode,
    publicationTimeZone,
    showCommentsButton,
  }: ExtendedSerializerProps,
  path = [0], // internal path tracking
  renderedMergeTags?: Set<string>,
): ReactNode => {
  const key = createKey([node, path]);
  // The components short-circuited below never reach getStructureElement, so they carry the node id from
  // here instead. Inert outside Studio, whose canvas is the only thing that reads it.
  const nodeId = node?.attrs?.id;
  const dataAttributes = {
    'data-json-path': path.join(','),
    ...(nodeId ? { 'data-node-id': String(nodeId) } : {}),
  };

  const isImage = node?.type === 'image';
  const isParagraph = node?.type === 'paragraph';
  const isHeading = node?.type === 'heading';
  const isAuthorImages = node?.type === 'authorImages';
  const isRootContainerImage = node?.type === 'container' && rootStyle && node?.attrs?.backgroundImage;

  if (isRootContainerImage) {
    node = {
      ...node,
      attrs: {
        ...node.attrs,
        backgroundImage: post?.image_url,
      },
    } as JSONContent & SerializableNode;
  }

  if (node?.type === 'postCardActions') {
    node = {
      ...node,
      attrs: {
        ...node.attrs,
        data: postInsights,
      },
    };
    return (
      <PostCardActionsElement
        key={key}
        postId={post.id}
        postSlug={post.slug}
        isLive={isLive}
        element={node as unknown as TPostCardActionsElement}
        attributes={dataAttributes}
        onLikeClick={onLikeClick}
        onCommentClick={onCommentClick}
        showCommentsButton={showCommentsButton}
      >
        {null}
      </PostCardActionsElement>
    );
  }

  // if node is container and content length is 1 and child node is post.paidContent, return null
  if (
    node?.type === 'container' &&
    node?.content?.length === 1 &&
    node?.content?.[0]?.attrs?.mergeTag === 'post.paidContent' &&
    !post.is_premium
  ) {
    return null;
  }

  // Only show paid content if the post is premium
  if (node.attrs?.mergeTag === 'post.paidContent' && !post.is_premium) {
    return null;
  }

  // Swap out images
  if (isImage && node.attrs?.mergeTag) {
    if (renderedMergeTags?.has(node.attrs.mergeTag)) return null;
    renderedMergeTags?.add(node.attrs.mergeTag);

    if (node.attrs.mergeTag === 'post.image') {
      node = {
        ...node,
        attrs: {
          ...node.attrs,
          alt: post.web_title,
          src: post?.image_url,
        },
      } as JSONContent & SerializableNode;
    }
  }

  // Swap out author images
  if (isAuthorImages) {
    return null;
  }

  // Swap out headings && paragraphs
  if ((isParagraph || isHeading) && node.attrs?.mergeTag) {
    if (renderedMergeTags?.has(node.attrs.mergeTag)) return null;
    renderedMergeTags?.add(node.attrs.mergeTag);

    if (node.attrs.mergeTag === 'post.title') {
      node = updateText(node, post?.web_title) as JSONContent & SerializableNode;
    }
    if (node?.attrs?.mergeTag === 'post.subtitle') {
      node = updateText(node, post?.web_subtitle || '') as JSONContent & SerializableNode;
    }
    if (node?.attrs?.mergeTag === 'post.timestamp') {
      node = updateText(
        node,
        formatPostTime(post?.override_scheduled_at, node.attrs?.postTimestampDisplay, publicationTimeZone),
      ) as JSONContent & SerializableNode;
    }
    if (node?.attrs?.mergeTag === 'post.readTime') {
      node = updateText(node, `${post?.estimated_reading_time || 3} ${node.attrs?.postReadTimeSuffix}`) as JSONContent &
        SerializableNode;
    }
    if (node?.attrs?.mergeTag === 'post.rank') {
      node = updateText(node, formatPostRank(postIndex || 0, node.attrs?.postRankFormat)) as JSONContent &
        SerializableNode;
    }
    if (node?.attrs?.mergeTag === 'post.authors') {
      return <PostAuthors key={key} authors={post?.authors || []} node={node} attributes={dataAttributes} />;
    }
    if (node?.attrs?.mergeTag === 'post.tags') {
      return (
        <ContentTags
          key={key}
          postNode={postNode}
          tags={post?.content_tags || []}
          node={node}
          isLive={isLive}
          isInsideLink
          attributes={dataAttributes}
        />
      );
    }
    if (node?.attrs?.mergeTag === 'post.paidContent') {
      return <PaidContent key={key} node={node} isPremium={post?.is_premium || false} attributes={dataAttributes} />;
    }
  }

  if ('text' in node) {
    return <SerializeTextLeaf key={key} leaf={node as TTextLeaf} />;
  }

  if (node?.type === 'button') {
    // This is to prevent hydration errors since this is nested in an anchor tag
    node = {
      ...node,
      attrs: {
        ...node.attrs,
        renderAsDiv: true,
      },
    } as JSONContent & SerializableNode;
  }

  const nodeChildren =
    node.content?.map((n, i) =>
      serializeNode(
        {
          node: n as JSONContent & SerializableNode,
          createKey,
          isImageOptimizable,
          post,
          postIndex,
          isLive,
          postInsights,
          onLikeClick,
          onCommentClick,
          postNode,
          publicationTimeZone,
          showCommentsButton,
        },
        [...path, i],
        renderedMergeTags,
      ),
    ) || [];

  // Skip containers whose children all resolved to null (e.g., all merge tags were duplicates)
  if (node?.type === 'container' && nodeChildren.length > 0 && nodeChildren.every((child) => child == null)) {
    return null;
  }

  return getStructureElement({
    node: node as SerializableNode,
    rootClassName,
    rootStyle,
    isImageOptimizable,
    key,
    children: [...nodeChildren, children],
    jsonPath: path,
  });
};

export function PostSerializer(props: ExtendedSerializerProps) {
  const context = useDreamComponentsContext();
  return <>{serializeNode({ ...props, publicationTimeZone: context.publicationTimeZone }, [0], new Set<string>())}</>;
}
